/**
 *
 * @typedef {import('../index').RulesProvider} RulesProvider
 * @typedef { import('../index').Rule } Rule
 * @typedef { import('../index').Context } Context
 * @typedef { import('../index').RulesEngine } Context
 */

/**
 *
 * @param {Rule[]} rules
 * @returns { RulesProvider } RulesProvider
 */
module.exports = class RulesArrayProvider {
    constructor(rules) {
        this.rules = rules;
    }

    /**
     *
     * @param {Context} context
     * @returns {Promise<Rule[]>}
     */
    async find({ verb, namespace, relation, status, featureFlags, ...context }) {
        return this.rules
            .filter(rule => {
                //first filter on any of the standard conditions:
                if (
                    !rule ||
                    //these have to match:
                    rule.verb !== verb ||
                    rule.status !== status ||
                    //IF these are defined on the rule, they have to match
                    !emptyOrMatch(rule.namespace, namespace) ||
                    !emptyOrMatch(rule.relation, relation)
                ) {
                    return false;
                }
                //then filter on any additional properties on the rule
                //E.g. to limit by tenant, user, or the user's role or other permissions
                const ruleCriteria = extractCriteria(rule);
                for (const key in ruleCriteria) {
                    if (Object.hasOwnProperty.call(ruleCriteria, key)) {
                        if (!emptyOrMatch(ruleCriteria[key], context[key])) return false;
                    }
                }
                //IF the rule has a featureFlag, that flag should also be on the context
                if (rule.featureFlag && (!featureFlags || !featureFlags.includes(rule.featureFlag))) return false;
                return true;
            })
            .sort((a, b) => {
                return (b.priority || 0) - (a.priority || 0);
            });
    }
};

const emptyOrMatch = (value1, value2) => {
    if (value1 === undefined) return true;
    if (Array.isArray(value1)) {
        return value1.includes(value2);
    }
    return value1 === value2;
};

/**
 * Everything left on a rule after the properties the engine owns is treated as a matching
 * criterion, compared against the same key on the context — which is how a rule limits itself by
 * tenant, user, role or anything else a caller puts there.
 *
 * So a property that is NOT a criterion has to be named here. Leave one out and the rule requires
 * the context to carry a matching value for it; no context does, so the rule matches nothing and
 * drops out of `find()` — with no error, and indistinguishable from a rule that simply did not
 * apply.
 *
 * `ruleId` is identity rather than a criterion: it names the rule, so no context would ever carry
 * it.
 */
const extractCriteria = rule => {
    // The names below are extracted only to leave them OUT of `criteria`, so none of them is read.
    // Disabled as a block rather than per line: `eslint-disable-next-line` covers one line, and the
    // destructure no longer fits on one.
    /* eslint-disable no-unused-vars */
    const {
        ruleId,
        verb,
        namespace,
        relation,
        status,
        featureFlag,
        priority,
        description,
        logic,
        onError,
        ...criteria
    } = rule;
    /* eslint-enable no-unused-vars */
    return criteria;
};
